import { useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { useDispatch } from 'react-redux';
import { X, Search, Trash2, Link2 } from 'lucide-react';
import { fetchOrdersDetailsById } from '../../store/thunk/ordersThunk';
import toast from 'react-hot-toast';
import { formatPrice } from '../../hooks/commonFunction';

const OrderMappingModal = ({ open, setOpen, onApply }) => {
  const dispatch = useDispatch();
  const [searchOrderId, setSearchOrderId] = useState('');
  const [mappedOrders, setMappedOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Parse partner info
  const getPartnerInfo = (partnerInfoString) => {
    try {
      if (!partnerInfoString) return null;
      const parsed = JSON.parse(partnerInfoString);
      return parsed?.partner?.[0] || null;
    } catch {
      return null;
    }
  };

  // Handle search order
  const handleSearchOrder = async () => {
    if (!searchOrderId.trim()) {
      toast.error('Please enter an order ID');
      return;
    }

    setIsSearching(true);
    try {
      const result = await dispatch(fetchOrdersDetailsById(searchOrderId.trim())).unwrap();

      if (result) {
        // Check if order already exists in mapped orders
        const orderExists = mappedOrders.some(
          (order) => order.order_id === result.order_id
        );

        if (orderExists) {
          toast.error('This order is already added');
        } else {
          setMappedOrders([...mappedOrders, result]);
          setSearchOrderId('');
          toast.success('Order added successfully');
        }
      } else {
        toast.error('Order not found');
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to fetch order details');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle delete order from list
  const handleDeleteOrder = (orderId) => {
    setMappedOrders(mappedOrders.filter((order) => order.order_id !== orderId));
    toast.success('Order removed from list');
  };

  // Handle apply mappings
  const handleApply = () => {
    if (mappedOrders.length === 0) {
      toast.error('Please add at least one order');
      return;
    }

    onApply(mappedOrders);
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    if (!isSearching) {
      setSearchOrderId('');
      setMappedOrders([]);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} className='relative z-50'>
      <DialogBackdrop className='fixed inset-0 bg-black/50' />

      <div className='fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto'>
        <DialogPanel className='bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col'>
          {/* Header */}
          <div className='flex items-center justify-between mb-4 sm:mb-6 bg-red-gradient p-3 sm:p-4'>
            <DialogTitle className='text-sm sm:text-md font-normal text-white flex items-center space-x-2 sm:space-x-3'>
              <Link2 className='h-5 w-5 sm:h-6 sm:w-6' />
              <span className='truncate'>Map Orders to Zelle Payment</span>
            </DialogTitle>
            <button
              onClick={handleClose}
              disabled={isSearching}
              className='text-white transition-colors hover:text-gray-200 disabled:opacity-50 flex-shrink-0'
            >
              <X size={20} className='sm:hidden' />
              <X size={24} className='hidden sm:block' />
            </button>
          </div>

          {/* Search Section */}
          <div className='px-3 sm:px-5 mb-4 sm:mb-6'>
            <label className='block text-xs sm:text-sm letter-spacing-07 font-medium text-gray-700 mb-2'>
              Search Order by ID
            </label>
            <div className='flex gap-2 sm:gap-3'>
              <div className='relative flex-1'>
                <input
                  type='text'
                  placeholder='Enter Order ID...'
                  value={searchOrderId}
                  onChange={(e) => setSearchOrderId(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchOrder();
                    }
                  }}
                  className='w-full border-2 border-gray-200 py-2.5 sm:py-3 px-3 sm:px-4 pr-10 shadow-sm text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 rounded-lg transition-all duration-200 bg-gray-50 hover:bg-white'
                  disabled={isSearching}
                />
                <Search className='absolute top-1/2 right-3 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400' />
              </div>
              <button
                onClick={handleSearchOrder}
                disabled={isSearching || !searchOrderId.trim()}
                className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-xs sm:text-sm tracking-wide transition-all duration-300 whitespace-nowrap ${
                  searchOrderId.trim() && !isSearching
                    ? 'bg-red-gradient text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSearching ? (
                  <span className='flex items-center justify-center'>
                    <svg
                      className='animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Searching...
                  </span>
                ) : (
                  'Search'
                )}
              </button>
            </div>
          </div>

          {/* Mapped Orders List */}
          <div className='flex-1 overflow-y-auto px-3 sm:px-5'>
            {mappedOrders.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 sm:py-12 text-gray-400'>
                <Search size={40} className='sm:w-12 sm:h-12 mb-3 opacity-50' />
                <p className='text-base sm:text-lg font-medium text-gray-500'>No orders added yet</p>
                <p className='text-xs sm:text-sm text-gray-400 mt-1 text-center px-4'>
                  Search and add orders using the search bar above
                </p>
              </div>
            ) : (
              <div className='space-y-3 sm:space-y-4 mb-4 sm:mb-6'>
                {mappedOrders.map((order) => {
                  const partnerInfo = getPartnerInfo(order.partner_info);
                  return (
                    <div
                      key={order.order_id}
                      className='relative bg-white border-2 border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow p-3 sm:p-5'
                    >
                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteOrder(order.order_id)}
                        className='absolute top-0 right-0 sm:top-3 sm:right-3 p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors z-10'
                        title='Remove order'
                      >
                        <Trash2 size={16} className='sm:w-[18px] sm:h-[18px]' />
                      </button>

                      {/* Order Details */}
                      <div className='grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 pr-8 sm:pr-10'>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order ID
                          </label>
                          <a className='text-sm sm:text-base font-bold text-primary-100 cursor-pointer'>
                            #{order.order_id}
                          </a>
                        </div>
                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Name
                          </label>
                          <p className='text-sm sm:text-base font-medium text-gray-900 truncate'>
                            {order.order_details?.name || 'N/A'}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Phone Number
                          </label>
                          <p className='text-xs sm:text-sm text-gray-900'>
                            {order.order_details?.phone_number || order.order_details?.formatted_phone_number || 'N/A'}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order Total
                          </label>
                          <p className='text-sm sm:text-md font-bold text-red-700'>
                            {formatPrice(order.order_total)}
                          </p>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Order Type
                          </label>
                          <span
                            className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                              order.order_type === 'delivery-partner'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {order.order_type || 'default'}
                          </span>
                        </div>

                        <div>
                          <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                            Payment Status
                          </label>
                          <span
                            className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                              order.payment_info?.payment_status === 'paid' || order.payment_info?.payment_status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : order.payment_info?.payment_status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {order.payment_info?.payment_status || 'N/A'}
                          </span>
                        </div>

                        {partnerInfo && (
                          <div className='col-span-2'>
                            <label className='block text-[10px] sm:text-xs letter-spacing-07 font-medium text-gray-500 mb-0.5 sm:mb-1'>
                              Partner Info
                            </label>
                            <div className='bg-gray-50 p-2 sm:p-3 rounded-md border border-gray-200'>
                              <p className='text-xs sm:text-sm font-medium text-gray-900'>
                                {partnerInfo.name}
                              </p>
                              <p className='text-[10px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1'>{partnerInfo.address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='border-t border-gray-200 p-3 sm:p-5 mt-3 sm:mt-4'>
            <div className='flex items-center justify-between mb-3 sm:mb-4'>
              <p className='text-xs sm:text-sm letter-spacing-07 text-gray-600'>
                {mappedOrders.length} order{mappedOrders.length !== 1 ? 's' : ''} added
              </p>
            </div>
            <div className='flex justify-center'>
              <button
                onClick={handleApply}
                disabled={mappedOrders.length === 0}
                className={`w-full sm:w-3/5 py-2.5 sm:py-3 rounded-lg font-medium text-sm sm:text-base transition-colors ${
                  mappedOrders.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'main-btn hover:opacity-90'
                }`}
              >
                Apply Mapping
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default OrderMappingModal;
